import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import Highlighter from 'react-highlight-words';
import styled from 'styled-components';

import { fetchAPI } from '../../utils/api';
import { BuyerWithEntity } from '../Entity/types';

const emptyBuyerEntity = {
  items: [],
  page: 1,
  per_page: 20,
  total: 0,
  loading: true,
  has_next: false,
  has_prev: false,
};

const QuotationBuyerEntity = (props: {
  visible: boolean;
  defaultValue?: {
    id: number;
    name: string;
  };
  buyerEntityId?: number;
  onSelect: (payload: {
    entity_buyer_id: number;
    buyer_name: string;
    buyer_email: string;
    buyer_phone: string;
    buyer_address: string;
    buyer_company_name: string;
  }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [filterEntity, setFilterEntity] = useState({ page: 1, q: '' });
  const debounceSearchBuyer = debounce(value => {
    setFilterEntity({ page: 1, q: value });
  }, 300);

  const buyerEntity = useQuery<{
    items: BuyerWithEntity[];
    page: number;
    per_page: number;
    total: number;
    has_prev: boolean;
    has_next: boolean;
    loading: boolean;
  }>(
    ['buyer-entity', props.visible, filterEntity.page, filterEntity.q],
    async () => {
      if (!props.visible) {
        return {
          ...emptyBuyerEntity,
          page: filterEntity.page,
        };
      }

      try {
        const resp = await fetchAPI('entity/buyer/list', {
          method: 'GET',
          // for future, can add filter regions
          payload: { q: filterEntity.q, page: filterEntity.page },
        });

        return {
          ...resp,
          loading: false,
          has_next: resp.page * resp.per_page < resp.total,
          has_prev: filterEntity.page > 1,
        };
      } catch (error) {
        return {
          ...emptyBuyerEntity,
          page: filterEntity.page,
          loading: false,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        ...emptyBuyerEntity,
        page: filterEntity.page,
      },
    }
  );

  const options = buyerEntity.data.items.map(obj => ({
    value: obj.id,
    label: (
      <OptionDiv>
        <p>
          <Highlighter
            searchWords={filterEntity.q.split(' ')}
            textToHighlight={obj.name}
          />
        </p>
        <b>
          <Highlighter
            searchWords={filterEntity.q.split(' ')}
            textToHighlight={obj.entity.name}
          />
        </b>
      </OptionDiv>
    ),
  }));

  if (buyerEntity.data.has_prev) {
    options.unshift({ value: -1, label: <span>{`<< Prev`}</span> });
  }
  if (buyerEntity.data.has_next) {
    options.push({
      value: -2,
      label: (
        <span
          style={{
            textAlign: 'right',
            width: '100%',
            display: 'inline-block',
          }}
        >{`Next >>`}</span>
      ),
    });
  }

  useEffect(() => {
    if (props.visible) {
      setSelectedValue(props.buyerEntityId || null);
      if (props.defaultValue?.id && props.defaultValue?.name) {
        setSelectedValue(props.defaultValue.id);
        setFilterEntity({ q: props.defaultValue.name, page: 1 });
      } else {
        setSelectedValue(null);
        setFilterEntity({ q: '', page: 1 });
      }
    }
  }, [props.visible]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Select
      open={open}
      onDropdownVisibleChange={vis => setOpen(vis)}
      value={selectedValue || undefined}
      placeholder={'Select Buyer from Buyer Entity Directory'}
      onSelect={value => {
        if ([-1, -2, -3].includes(value)) {
          if (value === -1) {
            // prev
            setFilterEntity(fe => ({ ...fe, page: fe.page - 1 }));
          } else if (value === -2) {
            // next
            setFilterEntity(fe => ({ ...fe, page: fe.page + 1 }));
          } else if (value === -3) {
            // still loading
          }

          setTimeout(() => {
            setOpen(true);
            setSelectedValue(null);
          }, [100]);
          return;
        }

        const selected = buyerEntity.data.items.find(obj => obj.id === value);
        props.onSelect({
          entity_buyer_id: value,
          buyer_name: selected?.name,
          buyer_email: selected?.email,
          buyer_phone: selected?.phone_number,
          buyer_address: selected?.shipping_address_1,
          buyer_company_name: selected?.entity.name,
        });
        setSelectedValue(value);
      }}
      allowClear
      onChange={value => {
        // handle deselect field
        if (!value) {
          setFilterEntity({ page: 1, q: '' });
        }
      }}
      onSearch={value => {
        debounceSearchBuyer(value);
      }}
      options={
        buyerEntity.isLoading || buyerEntity.data.loading
          ? [
              {
                value: -3,
                label: (
                  <span
                    style={{
                      textAlign: 'center',
                      width: '100%',
                      display: 'inline-block',
                    }}
                  >
                    <LoadingOutlined />
                  </span>
                ),
              },
            ]
          : options
      }
      showSearch
      filterOption={false}
    />
  );
};

const OptionDiv = styled.div`
  p {
    margin: 0;
  }

  b {
    font-size: 12px;
    color: #777;
  }
`;

export default QuotationBuyerEntity;
